import classNames from 'classnames';
import { DetailedHTMLProps, forwardRef, InputHTMLAttributes, PropsWithChildren } from 'react';

import CheckedSVG from './checkbox.svg';

import { WithFieldStateProps } from '../../hooks';

export interface CheckboxProps
  extends Omit<DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>, 'type'>,
    WithFieldStateProps {
  inputClassName?: string;
  color?: 'primary' | 'secondary';
  rounded?: 'default' | 'full' | 'none';
}

/**
 * Styled checkbox
 */
export const Checkbox = forwardRef<HTMLInputElement, PropsWithChildren<CheckboxProps>>((props, ref) => {
  const {
    children,
    color = 'primary',
    rounded = 'default',
    inputClassName,
    disabled,
    submitting,
    error,
    dirty,
    className,
    ...otherProps
  } = props;

  return (
    <label
      className={classNames(
        'relative flex items-center group',
        !disabled ? 'cursor-pointer' : 'cursor-default opacity-60 hover:bg-transparent',
        className,
      )}
    >
      <div className="flex h-5 items-center">
        <input
          {...otherProps}
          ref={ref}
          type="checkbox"
          className={classNames(
            'cursor-pointer disabled:cursor-default h-4 w-4 transition-colors ease-in-out duration-100',
            'appearance-none bg-surface border-solid border border-gray-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2',
            'bg-[length:0]',
            'bg-no-repeat bg-center',
            'checked:border-transparent disabled:checked:bg-neutral-500',
            color === 'primary' && 'text-brand-primary checked:bg-brand-primary',
            color === 'secondary' && 'text-gray-700 checked:bg-gray-700',
            rounded === 'default' && 'rounded',
            rounded === 'full' && 'rounded-full',
            inputClassName,
          )}
          disabled={disabled}
          style={{ backgroundImage: `url(${CheckedSVG})`, ...otherProps.style }}
        />
      </div>
      {children}
    </label>
  );
});

export default Checkbox;
